import {
  Component,
  forwardRef,
  inject,
  OnInit,
  signal,
  computed,
  DestroyRef,
  ViewChild,
  ElementRef,
  HostListener,
  effect,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { CountryService } from '../../services';
import { Country } from '../../shared/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  OverlayModule,
  CdkOverlayOrigin,
  CdkConnectedOverlay,
  ConnectedPosition,
} from '@angular/cdk/overlay';

/**
 * Componente combinado de país + teléfono con selector visual mejorado
 * Se comporta como un solo control visual pero maneja internamente país y número
 *
 * Valor emitido: { countryId: string, phoneNumber: string } | null
 */
@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    OverlayModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  templateUrl: './phone-input.html',
  styleUrl: './phone-input.css',
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit {
  private fb = inject(FormBuilder);
  private countryService = inject(CountryService);
  private destroyRef = inject(DestroyRef);

  form: FormGroup = this.fb.group({
    countryId: ['', Validators.required],
    phoneNumber: ['', Validators.required],
  });

  countries = signal<Country[]>([]);
  selectedCountry = signal<Country | null>(null);
  isLoading = signal(true);
  disabled = signal(false);

  // Dropdown state
  dropdownOpen = signal(false);
  searchTerm = signal('');
  highlightedIndex = signal<number>(-1);

  // Store countryId in a signal to trigger effect
  private countryIdValue = signal<string | null>(null);

  constructor() {
    // Effect: When countries load OR countryId changes, update selectedCountry
    // This handles the case when countryId is set before countries are loaded
    // Must be in constructor to be in injection context
    effect(() => {
      const countries = this.countries();
      const countryId = this.countryIdValue() || this.form.get('countryId')?.value;

      if (countries.length > 0 && countryId) {
        const country = countries.find((c) => c.countryId === countryId);
        const currentSelected = this.selectedCountry();

        // Update if country is found and different from current
        if (
          country &&
          (!currentSelected || currentSelected.countryId !== countryId)
        ) {
          this.selectedCountry.set(country);
          // Update phone number validation pattern
          const phoneControl = this.form.get('phoneNumber');
          if (phoneControl) {
            phoneControl.clearValidators();
            phoneControl.setValidators([
              Validators.required,
              Validators.pattern(country.phonePattern),
            ]);
            phoneControl.updateValueAndValidity();
          }
        } else if (!country && countryId) {
          // Country ID exists but country not found in list - might be loading issue
          // Don't clear, just wait for countries to be fully loaded
        }
      } else if (
        countries.length > 0 &&
        !countryId &&
        this.form.get('phoneNumber')?.value
      ) {
        // If we have countries, phone number, but no country selected, set default
        const defaultCountry =
          countries.find((c: Country) => c.isoCode === 'CO') || countries[0];
        if (defaultCountry && !this.form.get('countryId')?.value) {
          this.form.patchValue(
            { countryId: defaultCountry.countryId },
            { emitEvent: false }
          );
        }
      }
    });
  }

  // Overlay configuration
  overlayWidth = signal<string | number>('auto');
  overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  @ViewChild(CdkOverlayOrigin, { static: false })
  origin?: CdkOverlayOrigin;

  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;

  @ViewChild('countryList', { static: false })
  countryList?: ElementRef<HTMLDivElement>;

  @ViewChild('phoneInput', { static: false })
  phoneInput?: ElementRef<HTMLInputElement>;

  phoneMask = computed(() => this.selectedCountry()?.phoneMask || '');
  phonePlaceholder = computed(
    () => this.selectedCountry()?.phoneHolder || 'Ej: 300 123 4567'
  );

  filteredCountries = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const list = this.countries();
    if (!term) return list;
    return list.filter((country) => {
      const target =
        `${country.name} ${country.dialCode} ${country.isoCode}`.toLowerCase();
      return target.includes(term);
    });
  });

  private onChange = (
    value: { countryId: string; phoneNumber: string } | null
  ) => {};
  private onTouched = () => {};

  ngOnInit(): void {
    // Subscribe to countryId changes to update signal
    this.form
      .get('countryId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryId) => {
        this.countryIdValue.set(countryId || null);
      });

    // Set initial value
    this.countryIdValue.set(this.form.get('countryId')?.value || null);

    this.loadCountries();

    // Subscribe to form changes
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.form.valid) {
          const phoneValue = {
            countryId: value.countryId,
            phoneNumber: value.phoneNumber,
          };
          this.onChange(phoneValue);
        } else {
          this.onChange(null);
        }
      });

    // Subscribe to country changes to update mask and signal
    this.form
      .get('countryId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((countryId) => {
        // Update the signal to trigger the effect
        this.countryIdValue.set(countryId || null);

        const country = this.countries().find((c) => c.countryId === countryId);
        this.selectedCountry.set(country || null);

        // Update phone number validation pattern
        if (country) {
          const phoneControl = this.form.get('phoneNumber');
          if (phoneControl) {
            // Remove old validators
            phoneControl.clearValidators();
            // Add new validators with country-specific pattern
            phoneControl.setValidators([
              Validators.required,
              Validators.pattern(country.phonePattern),
            ]);
            phoneControl.updateValueAndValidity();
          }
        }
      });
  }

  private async loadCountries(): Promise<void> {
    try {
      this.isLoading.set(true);
      // Fetch countries and set to store
      this.countryService.fetchAndSetEntitiesToStore();

      // The store fills when the request answers, which can take seconds on a slow network:
      // wait for it instead of a fixed delay, otherwise the list stays empty
      const countriesStore = this.countryService.getStore().getModel();
      const deadline = Date.now() + 15000;
      let countries = countriesStore();
      while ((!countries || countries.length === 0) && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        countries = countriesStore();
      }

      if (countries && countries.length > 0) {
        this.applyCountries(countries);
      }
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private applyCountries(countries: Country[]): void {
    this.countries.set(countries);

    // After countries are loaded, check if we have a countryId set and update selectedCountry
    const countryId = this.form.get('countryId')?.value;
    if (countryId) {
      const country = countries.find((c) => c.countryId === countryId);
      if (country) {
        this.selectedCountry.set(country);
        // Update validation pattern
        const phoneControl = this.form.get('phoneNumber');
        if (phoneControl) {
          phoneControl.clearValidators();
          phoneControl.setValidators([
            Validators.required,
            Validators.pattern(country.phonePattern),
          ]);
          phoneControl.updateValueAndValidity();
        }
      }
    } else {
      // Set default country (Colombia if available, otherwise first country)
      const defaultCountry =
        countries.find((c: Country) => c.isoCode === 'CO') || countries[0];
      if (defaultCountry) {
        this.form.patchValue(
          { countryId: defaultCountry.countryId },
          { emitEvent: false }
        );
      }
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: { countryId: string; phoneNumber: string } | null): void {
    if (!value) {
      this.form.reset({ emitEvent: false });
      this.selectedCountry.set(null);
      return;
    }

    const { countryId, phoneNumber } = value;

    // Always set phone number if provided
    if (phoneNumber !== undefined && phoneNumber !== null) {
      this.form.patchValue({ phoneNumber }, { emitEvent: false });
    }

    // Handle country ID - always set it, even if countries aren't loaded yet
    // The effect and loadCountries will update selectedCountry when countries load
    if (countryId) {
      this.form.patchValue({ countryId }, { emitEvent: false });
      // Update the signal to trigger the effect
      this.countryIdValue.set(countryId);

      // If countries are already loaded, update selectedCountry immediately
      const countries = this.countries();
      if (countries.length > 0) {
        const country = countries.find((c) => c.countryId === countryId);
        if (country) {
          this.selectedCountry.set(country);
          // Update validation pattern
          const phoneControl = this.form.get('phoneNumber');
          if (phoneControl) {
            phoneControl.clearValidators();
            phoneControl.setValidators([
              Validators.required,
              Validators.pattern(country.phonePattern),
            ]);
            phoneControl.updateValueAndValidity();
          }
        } else {
          // Country ID not found in loaded countries - clear selectedCountry
          // It will be set when countries load or when effect runs
          this.selectedCountry.set(null);
        }
      }
      // If countries are not loaded yet, selectedCountry will be set by loadCountries or effect
    } else if (phoneNumber && !countryId) {
      // Only phone number provided, use default country when available
      const countries = this.countries();
      if (countries.length > 0) {
        const defaultCountry =
          countries.find((c: Country) => c.isoCode === 'CO') || countries[0];
        if (defaultCountry) {
          this.form.patchValue(
            { countryId: defaultCountry.countryId },
            { emitEvent: false }
          );
        }
      }
    }
  }

  registerOnChange(
    fn: (value: { countryId: string; phoneNumber: string } | null) => void
  ): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  // Dropdown methods
  toggleDropdown(): void {
    if (this.disabled()) return;
    const wasOpen = this.dropdownOpen();
    this.dropdownOpen.set(!wasOpen);
    if (this.dropdownOpen()) {
      this.onTouched();
      this.highlightedIndex.set(-1);
      this.searchTerm.set('');
      queueMicrotask(() => {
        // Set minimum width for dropdown to show full country names
        const minWidth = 320; // Minimum width in pixels
        const originWidth =
          this.origin?.elementRef.nativeElement.getBoundingClientRect().width ??
          0;
        const width = Math.max(originWidth, minWidth);
        this.overlayWidth.set(width);
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = '';
          this.searchInput.nativeElement.focus();
        }
      });
    }
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    this.highlightedIndex.set(-1);
  }

  selectCountry(country: Country): void {
    if (this.disabled()) return;
    this.form.patchValue({ countryId: country.countryId }, { emitEvent: true });
    this.dropdownOpen.set(false);
    this.searchTerm.set('');
    this.highlightedIndex.set(-1);
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    // Focus phone input after selection
    queueMicrotask(() => {
      this.phoneInput?.nativeElement?.focus();
    });
  }

  onCountryButtonKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.dropdownOpen()) {
          this.toggleDropdown();
        }
        break;
      case 'Escape':
        if (this.dropdownOpen()) {
          event.preventDefault();
          this.dropdownOpen.set(false);
          this.highlightedIndex.set(-1);
        }
        break;
      default:
        // If typing a character, open dropdown and focus search input
        if (
          event.key.length === 1 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey
        ) {
          event.preventDefault();
          if (!this.dropdownOpen()) {
            this.toggleDropdown();
          }
          queueMicrotask(() => {
            if (this.searchInput?.nativeElement) {
              this.searchInput.nativeElement.value = event.key;
              this.searchTerm.set(event.key);
              this.searchInput.nativeElement.focus();
              this.searchInput.nativeElement.setSelectionRange(1, 1);
            }
          });
        }
        break;
    }
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    const filtered = this.filteredCountries();
    const maxIndex = filtered.length - 1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex.update((idx) => (idx < maxIndex ? idx + 1 : 0));
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex.update((idx) => (idx > 0 ? idx - 1 : maxIndex));
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        if (
          this.highlightedIndex() >= 0 &&
          this.highlightedIndex() < filtered.length
        ) {
          this.selectCountry(filtered[this.highlightedIndex()]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.dropdownOpen.set(false);
        this.highlightedIndex.set(-1);
        break;
    }
  }

  onCountryItemKeyDown(
    event: KeyboardEvent,
    country: Country,
    index: number
  ): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectCountry(country);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const filtered = this.filteredCountries();
        this.highlightedIndex.set(index < filtered.length - 1 ? index + 1 : 0);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const filteredUp = this.filteredCountries();
        this.highlightedIndex.set(
          index > 0 ? index - 1 : filteredUp.length - 1
        );
        this.scrollToHighlighted();
        break;
    }
  }

  private scrollToHighlighted(): void {
    queueMicrotask(() => {
      if (this.countryList?.nativeElement && this.highlightedIndex() >= 0) {
        const items = this.countryList.nativeElement.querySelectorAll('button');
        const highlightedItem = items[this.highlightedIndex()] as HTMLElement;
        if (highlightedItem) {
          highlightedItem.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          });
        }
      }
    });
  }

  protected flagEmoji(isoCode?: string): string {
    if (!isoCode) return '🏳️';
    const upper = isoCode.toUpperCase();
    return upper.replace(/[A-Z]/g, (char) =>
      String.fromCodePoint(char.charCodeAt(0) + 127397)
    );
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent) {
    if (!this.dropdownOpen()) return;
    const target = event.target as HTMLElement;
    if (
      !target.closest('.phone-input-wrapper') &&
      !target.closest('.phone-country-dropdown')
    ) {
      this.dropdownOpen.set(false);
    }
  }

  get isInvalid(): boolean {
    const countryInvalid =
      this.form.get('countryId')?.invalid &&
      this.form.get('countryId')?.touched;
    const phoneInvalid =
      this.form.get('phoneNumber')?.invalid &&
      this.form.get('phoneNumber')?.touched;
    return !!(countryInvalid || phoneInvalid);
  }

  get errorMessage(): string {
    if (this.form.get('countryId')?.hasError('required')) {
      return 'Selecciona un país';
    }
    if (this.form.get('phoneNumber')?.hasError('required')) {
      return 'El teléfono es requerido';
    }
    if (this.form.get('phoneNumber')?.hasError('pattern')) {
      return 'El formato del teléfono no es válido';
    }
    return '';
  }
}
